import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { clearProfitQuarterReport } from "../../../Redux/Reports/ReportsReducer";
import { getProfitQuarterReport } from "../../../Redux/Reports/ReportsActions";
import { getAllBuildings } from "../../../Redux/Buildings/BuildingActions";
import Loading from "../../../Components/Common/Loading/Loading";
import { AppDispatch, RootState } from "../../../Redux/store";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toastHelper from "../../../Util/toastHelper";
import makeAnimated from "react-select/animated";
import { Switch } from "react-animated-switch";
import "./ProfitQuarterReport.css";
import html2pdf from "html2pdf.js";
import Select from "react-select";
import moment from "moment";

const animatedComponents = makeAnimated();

const roundToTwoDecimals = (value: number): number =>
  Number((value || 0).toFixed(2));

const formatToTwoDecimals = (value: number | undefined | null): string =>
  roundToTwoDecimals(Number(value)).toFixed(2);

const optionItems = [
  { label: "Current Month", value: "currentMonth" },
  { label: "Last two months", value: "2months" },
  { label: "Last three months", value: "3months" },
];

function ProfitQuarterReport() {
  const pdfRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const [selectedBuilding, setSelectedBuilding] = useState<any[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [isNonMember, setIsNonMember] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [customDateRange, setCustomDateRange] = useState<boolean>(false);
  const { buildings, buildingsLoading } = useSelector(
    (state: RootState) => state.buildings,
  );
  const { profitQuarterReport, profitQuarterReportLoading } = useSelector(
    (state: RootState) => state.reports,
  );

  const optionsToDates = (
    monthOption: string,
  ): { startDate: string; endDate: string } => {
    if (monthOption === "currentMonth") {
      return {
        startDate: moment().startOf("month").format("YYYY-MM-DD"),
        endDate: moment().endOf("month").format("YYYY-MM-DD"),
      };
    }

    if (monthOption === "2months") {
      return {
        startDate: moment()
          .subtract(2, "months")
          .startOf("month")
          .format("YYYY-MM-DD"),
        endDate: moment()
          .subtract(1, "months")
          .endOf("month")
          .format("YYYY-MM-DD"),
      };
    }

    if (monthOption === "3months") {
      return {
        startDate: moment()
          .subtract(3, "months")
          .startOf("month")
          .format("YYYY-MM-DD"),
        endDate: moment()
          .subtract(1, "months")
          .endOf("month")
          .format("YYYY-MM-DD"),
      };
    }

    return {
      startDate: moment().startOf("month").format("YYYY-MM-DD"),
      endDate: moment().endOf("month").format("YYYY-MM-DD"),
    };
  };

  interface BuildingData {
    month: string;
    noOfMemberships: number;
  }

  interface Building {
    buildingName: string;
    data: BuildingData[];
  }

  function calculateTotalMembersByMonth(
    buildings: Building[],
  ): { month: string; noOfMemberships: number }[] {
    const totals: Record<string, number> = {};

    buildings?.forEach((building) => {
      building?.data?.forEach(({ month, noOfMemberships }) => {
        totals[month] = (totals[month] || 0) + noOfMemberships;
      });
    });

    return Object.entries(totals).map(([month, noOfMemberships]) => ({
      month,
      noOfMemberships,
    }));
  }

  interface BookingData {
    month: string;
    noOfBookings: number;
  }

  interface Booking {
    buildingName: string;
    data: BookingData[];
  }

  function calculateTotalBookingsByMonth(
    buildings: Booking[],
  ): { month: string; noOfBookings: number }[] {
    const totals: Record<string, number> = {};

    buildings?.forEach((building) => {
      building?.data?.forEach(({ month, noOfBookings }) => {
        totals[month] = (totals[month] || 0) + noOfBookings;
      });
    });

    return Object.entries(totals).map(([month, noOfBookings]) => ({
      month,
      noOfBookings,
    }));
  }

  interface RevenueData {
    month: string;
    revenue: number;
  }

  interface RevenueBuilding {
    buildingName: string;
    data: RevenueData[];
  }

  function calculateTotalRevenueByMonth(
    buildings: RevenueBuilding[],
  ): { month: string; revenue: number }[] {
    const totals: Record<string, number> = {};

    buildings?.forEach((building) => {
      building?.data?.forEach(({ month, revenue }) => {
        totals[month] = roundToTwoDecimals((totals[month] || 0) + revenue);
      });
    });

    return Object.entries(totals).map(([month, revenue]) => ({
      month,
      revenue: roundToTwoDecimals(revenue),
    }));
  }

  useEffect(() => {
    dispatch(getAllBuildings());

    return () => {
      dispatch(clearProfitQuarterReport());
    };
  }, []);

  const getReportHandler = () => {
    if (!selectedBuilding) {
      toastHelper.error("Please select a building");
    }
    if (customDateRange && (!startDate || !endDate)) {
      toastHelper.error("Please select a start and end date");
      return;
    }
    if (!customDateRange && !selectedMonth) {
      toastHelper.error("Please select a month");
      return;
    }

    if (profitQuarterReportLoading) {
      return;
    }
    if (selectedMonth) {
      dispatch(
        getProfitQuarterReport({
          buildingIds: selectedBuilding?.map((building: any) =>
            Number(building.value),
          ),
          startDate: optionsToDates(selectedMonth).startDate,
          endDate: optionsToDates(selectedMonth).endDate,
          isNonMember,
        }),
      );
    }
    if (customDateRange) {
      dispatch(
        getProfitQuarterReport({
          buildingIds: selectedBuilding?.map((building: any) =>
            Number(building.value),
          ),
          startDate: startDate,
          endDate: endDate,
          isNonMember,
        }),
      );
    }
  };

  const options: any = buildings?.map((building: any) => ({
    label: building.buildingName,
    value: building.buildingId.toString(),
    data: building,
  }));

  const downloadPdfHandler = async () => {
    if (!pdfRef.current) return;

    const element = pdfRef.current as HTMLElement;

    // Save original styles
    const originalWidth = element.style.width;
    const originalMaxWidth = element.style.maxWidth;

    // Force a wide desktop layout
    element.style.width = "1500px";
    element.style.maxWidth = "1500px";
    element.style.padding = "20px";

    // Wait for layout to update
    await new Promise((resolve) => setTimeout(resolve, 500));

    const width = element.scrollWidth;
    const height = element.scrollHeight;

    const options: any = {
      margin: 0,
      filename: `ProfitQuarterReport-${moment().format("DD-MM-YYYY")}.pdf`,
      image: {
        type: "jpeg",
        quality: 1,
      },
      html2canvas: {
        scale: 3,
        useCORS: true,
        windowWidth: 2000,
        scrollX: 0,
        scrollY: 0,
      },
      jsPDF: {
        unit: "px",
        format: [width, height],
        orientation: "portrait",
        compress: true,
      },
      pagebreak: {
        mode: [],
      },
    };

    try {
      await html2pdf().set(options).from(element).save();
    } finally {
      // Restore original styles
      element.style.width = originalWidth;
      element.style.maxWidth = originalMaxWidth;
    }
  };

  return (
    <React.Fragment>
      {(buildingsLoading || profitQuarterReportLoading) && <Loading />}
      <div className="pqr-main-container">
        <div className="pqr-heading-container">
          <h1 className="pqr-heading">Quarterly Profit Report</h1>
        </div>
        <div className="pqr-top-container">
          <div className="pqr-input-container">
            <Select
              classNamePrefix="pqr-custom-select"
              closeMenuOnSelect={false}
              components={animatedComponents}
              value={selectedBuilding}
              isOptionDisabled={() => selectedBuilding.length === 3}
              isMulti
              options={options ?? []}
              onChange={(e: any) => {
                if (e?.length <= 3) {
                  setSelectedBuilding(e);
                } else {
                  toastHelper.error("You can select up to 3 buildings");
                }
              }}
            />
          </div>
          {!customDateRange && (
            <div className="pqr-input-container">
              <select
                className="pqr-input"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {!selectedMonth && <option value="">Select a month</option>}
                {optionItems.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {customDateRange && (
            <React.Fragment>
              <div className="pqr-input-container">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    if (endDate) {
                      if (moment(e.target.value).isAfter(moment(endDate))) {
                        toastHelper.error(
                          "Start date cannot be after end date",
                        );
                        return;
                      }
                      if (
                        moment(e.target.value).isBefore(
                          moment(endDate).clone().subtract(3, "months"),
                        )
                      ) {
                        toastHelper.error(
                          "Start date cannot be more than 3 months before the end date",
                        );
                        return;
                      }
                    }
                    setStartDate(e.target.value);
                  }}
                  className="pqr-input"
                />
              </div>
              <div className="pqr-input-container">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    if (startDate) {
                      if (moment(e.target.value).isBefore(moment(startDate))) {
                        toastHelper.error(
                          "End date cannot be before start date",
                        );
                        return;
                      }

                      if (
                        moment(e.target.value).isAfter(
                          moment(startDate).clone().add(3, "months"),
                        )
                      ) {
                        toastHelper.error(
                          "End date cannot be more than 3 months after the start date",
                        );
                        return;
                      }
                    }

                    setEndDate(e.target.value);
                  }}
                  className="pqr-input"
                />
              </div>
            </React.Fragment>
          )}

          <div className="pqr-switch-container">
            <p className="pqr-text">Custom Date Range</p>
            <Switch
              checked={customDateRange}
              onChange={(val) => {
                setStartDate("");
                setEndDate("");
                setSelectedMonth("");
                setCustomDateRange(val);
              }}
              activeTrackColor="#000000"
            />
          </div>
          <div className="pqr-switch-container">
            <p className="pqr-text">Is Non Member</p>
            <Switch
              checked={isNonMember}
              onChange={(val) => setIsNonMember(val)}
              activeTrackColor="#000000"
            />
          </div>
          <div className="pqr-button-container">
            <button className="pqr-button" onClick={getReportHandler}>
              Get Report
            </button>
          </div>
          {profitQuarterReport && (
            <div className="pqr-button-container">
              <button className="pqr-button" onClick={downloadPdfHandler}>
                Download PDF
              </button>
            </div>
          )}
        </div>
        {profitQuarterReport && (
          <div className="pqr-data-container" ref={pdfRef}>
            <div className="pqr-main-heading-container">
              <h1 className="pqr-heading">Performance Summary</h1>
              <div className="pqr-building-name-container">
                {profitQuarterReport?.memberships?.map(
                  (item: any, i: number) => {
                    return (
                      <h1 key={i} className="pqr-building-name">
                        {item?.buildingName}
                      </h1>
                    );
                  },
                )}
              </div>
            </div>
            <div className="pqr-heading-container">
              <div className="pqr-design-container"></div>
              <div className="pqr-heading-content">
                <h2 className="pqr-heading">Memberships</h2>
                <p className="pqr-sub-heading">
                  Number of members who joined or cancelled their membership
                  during the selected period.
                </p>
              </div>
            </div>
            <div className="pqr-charts-container">
              {profitQuarterReport?.memberships?.map(
                (membership: any, i: number) => {
                  return (
                    <div key={i} className="pqr-chart">
                      <p className="pqr-chart-title">
                        {membership?.buildingName}
                      </p>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={membership?.data}
                          width="100%"
                          margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                        >
                          <XAxis
                            dataKey="month"
                            label={{
                              value: "Month",
                              position: "insideBottom",
                              offset: -10,
                            }}
                          />
                          <YAxis
                            allowDecimals={false}
                            label={{
                              value: "No. of Memberships",
                              angle: -90,
                              position: "insideLeft",
                              style: { textAnchor: "middle" },
                            }}
                          />
                          <Tooltip
                            cursor={{ fill: "rgba(0,0,0,0.03)" }}
                            content={({ active, payload }) => {
                              if (!active || !payload?.length) return null;

                              return (
                                <div
                                  style={{
                                    background: "#fff",
                                    padding: "6px 10px",
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  {payload[0]?.payload?.noOfMemberships}
                                </div>
                              );
                            }}
                          />
                          <Bar dataKey="noOfMemberships" fill="#F4B7B8" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                },
              )}
            </div>
            <div className="pqr-heading-container">
              <div className="pqr-design-container"></div>
              <div className="pqr-heading-content">
                <h2 className="pqr-heading">Memberships Revenue</h2>
                <p className="pqr-sub-heading">
                  Total income from memberships during the selected period.
                </p>
              </div>
            </div>
            <div className="pqr-revenue-main-container">
              <div className="pqr-revenue-container">
                {profitQuarterReport?.membershipsRevenue?.map(
                  (revenue: any, i: number) => {
                    return revenue?.data?.map((item: any, i: number) => {
                      return (
                        <div key={i} className="pqr-revenue-item">
                          <p className="pqr-revenue">
                            ${formatToTwoDecimals(item?.revenue)}
                          </p>
                          <p className="pqr-month">{item?.month}</p>
                        </div>
                      );
                    });
                  },
                )}
              </div>
            </div>
            <div className="pqr-table-container">
              <table className="pqr-light-table">
                <td>
                  <p className="pqr-total-members">Total Members</p>
                  <p className="pqr-total-members-text">
                    Total members of all buildings
                  </p>
                </td>
                {calculateTotalMembersByMonth(
                  profitQuarterReport?.memberships,
                )?.map((item, i: number) => {
                  return (
                    <td key={i}>
                      <p className="pqr-month-member-container">
                        <span className="pqr-month">
                          {moment(item?.month, "MMM").format("MMMM")}
                        </span>
                        <span className="pqr-no-of-member">
                          ({item?.noOfMemberships})
                        </span>
                      </p>
                    </td>
                  );
                })}
              </table>
            </div>

            <div className="pqr-table-container">
              <table className="pqr-dark-table">
                <td>
                  <p className="pqr-total-members">Total Revenue</p>
                  <p className="pqr-total-members-text">
                    Total revenue of all buildings
                  </p>
                </td>
                {calculateTotalRevenueByMonth(
                  profitQuarterReport?.membershipsRevenue,
                )?.map((item, i: number) => {
                  return (
                    <td key={i}>
                      <p className="pqr-month-member-container">
                        <span className="pqr-month">
                          {moment(item?.month, "MMM").format("MMMM")}-
                        </span>
                        <span className="pqr-no-of-member">
                          ${formatToTwoDecimals(item?.revenue)}
                        </span>
                      </p>
                    </td>
                  );
                })}
              </table>
            </div>

            <div className="pqr-heading-container">
              <div className="pqr-design-container"></div>
              <div className="pqr-heading-content">
                <h2 className="pqr-heading">Events</h2>
                <p className="pqr-sub-heading">
                  Number of paid bookings during the selected period.
                </p>
              </div>
            </div>
            <div className="pqr-charts-container">
              {profitQuarterReport?.bookings?.map((booking: any, i: number) => {
                return (
                  <div key={i} className="pqr-chart">
                    <p className="pqr-chart-title">{booking?.buildingName}</p>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={booking?.data}
                        width="100%"
                        margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                      >
                        <XAxis dataKey="month" />
                        <YAxis
                          allowDecimals={false}
                          label={{
                            value: "No. of Bookings",
                            angle: -90,
                            position: "insideLeft",
                            style: { textAnchor: "middle" },
                          }}
                        />
                        <Tooltip
                          cursor={{ fill: "rgba(0,0,0,0.03)" }}
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;

                            return (
                              <div
                                style={{
                                  background: "#fff",
                                  padding: "6px 10px",
                                  border: "1px solid #ddd",
                                }}
                              >
                                {payload[0]?.payload?.noOfBookings}
                              </div>
                            );
                          }}
                        />
                        <Bar dataKey="noOfBookings" fill="#F4B7B8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })}
            </div>
            <div className="pqr-heading-container">
              <div className="pqr-design-container"></div>
              <div className="pqr-heading-content">
                <h2 className="pqr-heading">Events Revenue</h2>
                <p className="pqr-sub-heading">
                  Total income from bookings during the selected period.
                </p>
              </div>
            </div>
            <div className="pqr-revenue-main-container">
              <div className="pqr-revenue-container">
                {profitQuarterReport?.bookingsRevenue?.map(
                  (revenue: any, i: number) => {
                    return revenue?.data?.map((item: any, i: number) => {
                      return (
                        <div key={i} className="pqr-revenue-item">
                          <p className="pqr-revenue">
                            ${formatToTwoDecimals(item?.revenue)}
                          </p>
                          <p className="pqr-month">{item?.month}</p>
                        </div>
                      );
                    });
                  },
                )}
              </div>
            </div>

            <div className="pqr-table-container">
              <table className="pqr-light-table">
                <td>
                  <p className="pqr-total-members">Total Events</p>
                  <p className="pqr-total-members-text">
                    Total events of all buildings
                  </p>
                </td>
                {calculateTotalBookingsByMonth(
                  profitQuarterReport?.bookings,
                )?.map((item, i: number) => {
                  return (
                    <td key={i}>
                      <p className="pqr-month-member-container">
                        <span className="pqr-month">
                          {moment(item?.month, "MMM").format("MMMM")}
                        </span>
                        <span className="pqr-no-of-member">
                          ({item?.noOfBookings})
                        </span>
                      </p>
                    </td>
                  );
                })}
              </table>
            </div>

            <div className="pqr-table-container">
              <table className="pqr-dark-table">
                <td>
                  <p className="pqr-total-members">Total Revenue</p>
                  <p className="pqr-total-members-text">
                    Total revenue of all buildings
                  </p>
                </td>
                {calculateTotalRevenueByMonth(
                  profitQuarterReport?.bookingsRevenue,
                )?.map((item, i: number) => {
                  return (
                    <td key={i}>
                      <p className="pqr-month-member-container">
                        <span className="pqr-month">
                          {moment(item?.month, "MMM").format("MMMM")}-
                        </span>
                        <span className="pqr-no-of-member">
                          ${formatToTwoDecimals(item?.revenue)}
                        </span>
                      </p>
                    </td>
                  );
                })}
              </table>
            </div>
            <div className="pqr-main-heading-container">
              <h1 className="pqr-heading">Revenue Statement</h1>
              <div className="pqr-building-name-container">
                {profitQuarterReport?.bookings?.map((item: any, i: number) => {
                  return (
                    <h1 key={i} className="pqr-building-name">
                      {item?.buildingName}
                    </h1>
                  );
                })}
              </div>
            </div>

            {profitQuarterReport?.revenueData &&
              profitQuarterReport?.revenueData?.length > 0 &&
              profitQuarterReport?.revenueData?.map((item: any, i: number) => {
                return (
                  <div className="pqr-revenue-statement-container">
                    <div className="pqr-heading-container">
                      <h4 className="pqr-heading">{item?.buildingName}</h4>
                    </div>
                    <div className="pqr-revenue-statement-item">
                      <div className="pqr-revenue-statement-item-inline">
                        <p className="pqr-revenue-heading">
                          Membership Revenue
                        </p>
                        <p className="pqr-revenue-text">
                          ${formatToTwoDecimals(item?.memberRevenue)}
                        </p>
                      </div>
                      <div className="pqr-revenue-statement-item-inline">
                        <p className="pqr-revenue-heading">Events Revenue</p>
                        <p className="pqr-revenue-text">
                          ${formatToTwoDecimals(item?.bookingRevenue)}
                        </p>
                      </div>
                    </div>
                    <div className="pqr-revenue-share-container">
                      <div className="pqr-revenue-share-item">
                        <div className="pqr-revenue-share-item-inline">
                          <p className="pqr-revenue-share-heading">
                            Amenigies Share
                          </p>
                          <p className="pqr-revenue-share-text">
                            {formatToTwoDecimals(item?.amenigiesShare)}%
                          </p>
                        </div>
                        <div className="pqr-revenue-share-item-inline">
                          <p className="pqr-revenue-share-heading">Value</p>
                          <p className="pqr-revenue-share-text">
                            ${formatToTwoDecimals(item?.amenigiesShareValue)}
                          </p>
                        </div>
                      </div>
                      <div className="pqr-revenue-share-item">
                        <div className="pqr-revenue-share-item-inline">
                          <p className="pqr-revenue-share-heading">
                            Owner Share
                          </p>
                          <p className="pqr-revenue-share-text">
                            {formatToTwoDecimals(item?.ownerShare)}%
                          </p>
                        </div>
                        <div className="pqr-revenue-share-item-inline">
                          <p className="pqr-revenue-share-heading">Value</p>
                          <p className="pqr-revenue-share-text">
                            ${formatToTwoDecimals(item?.ownerShareValue)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            <div className="pqr-revenue-statement-container">
              <div className="pqr-heading-container">
                <h4 className="pqr-heading">All buildings</h4>
              </div>
              <div className="pqr-revenue-statement-item">
                <div className="pqr-revenue-statement-item-inline">
                  <p className="pqr-revenue-heading">Total Revenue</p>
                  <p className="pqr-revenue-text">
                    $
                    {formatToTwoDecimals(
                      profitQuarterReport?.totalRevenueData?.totalRevenue,
                    )}
                  </p>
                </div>
              </div>
              <div className="pqr-revenue-share-container">
                <div className="pqr-revenue-share-item">
                  <div className="pqr-revenue-share-item-inline">
                    <p className="pqr-revenue-share-heading">Amenigies Share</p>
                    <p className="pqr-revenue-share-text">
                      {formatToTwoDecimals(
                        profitQuarterReport?.totalRevenueData?.amenigiesShare,
                      )}
                      %
                    </p>
                  </div>
                  <div className="pqr-revenue-share-item-inline">
                    <p className="pqr-revenue-share-heading">Value</p>
                    <p className="pqr-revenue-share-text">
                      $
                      {formatToTwoDecimals(
                        profitQuarterReport?.totalRevenueData
                          ?.amenigiesShareValue,
                      )}
                    </p>
                  </div>
                </div>
                <div className="pqr-revenue-share-item">
                  <div className="pqr-revenue-share-item-inline">
                    <p className="pqr-revenue-share-heading">Owner Share</p>
                    <p className="pqr-revenue-share-text">
                      {formatToTwoDecimals(
                        profitQuarterReport?.totalRevenueData?.ownerShare,
                      )}
                      %
                    </p>
                  </div>
                  <div className="pqr-revenue-share-item-inline">
                    <p className="pqr-revenue-share-heading">Value</p>
                    <p className="pqr-revenue-share-text">
                      $
                      {formatToTwoDecimals(
                        profitQuarterReport?.totalRevenueData?.ownerShareValue,
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="pqr-design-line"></div>
          </div>
        )}
        {!profitQuarterReport && (
          <div className="pqr-no-data-container">
            <p className="pqr-no-data-text">No data available</p>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}

export default ProfitQuarterReport;
